import { useEffect, useMemo, useRef } from "react";
import Select from "components/Ui/SelectField/SelectField";
import useCategories from "hooks/useCateogries";

const confidenceClass = (c) => {
    if (typeof c !== "number") return "mid";
    if (c >= 0.85) return "high";
    if (c >= 0.6) return "mid";
    return "low";
};

const confidenceLabel = (c) => (typeof c === "number" ? c.toFixed(2) : "—");

const ReviewView = ({ extracted, setExtracted, onCommit, onStartOver, committing }) => {
    const { categories, fetchCategories } = useCategories();

    // Fetch categories once on mount. Using a ref to guard against the
    // double-invocation StrictMode + the fact that fetchCategories isn't
    // memoized in the hook — adding it to deps would re-fire forever.
    const didFetchCategoriesRef = useRef(false);
    useEffect(() => {
        if (didFetchCategoriesRef.current) return;
        if (!categories || categories.length === 0) {
            didFetchCategoriesRef.current = true;
            fetchCategories?.();
        }
    }, [categories, fetchCategories]);

    const categoryOptions = useMemo(
        () => (categories ?? []).map((c) => ({ value: c.id, label: c.name })),
        [categories],
    );

    const updateItem = (imgIdx, itemIdx, patch) => {
        setExtracted((prev) => {
            const next = { ...prev, images: [...prev.images] };
            const img = { ...next.images[imgIdx] };
            img.items = img.items.map((it, i) => (i === itemIdx ? { ...it, ...patch } : it));
            next.images[imgIdx] = img;
            return next;
        });
    };

    const setBulkApprovedForImage = (imgIdx, predicate) => {
        setExtracted((prev) => {
            const next = { ...prev, images: [...prev.images] };
            const img = { ...next.images[imgIdx] };
            img.items = img.items.map((it) => ({ ...it, approved: predicate(it) }));
            next.images[imgIdx] = img;
            return next;
        });
    };

    const totalApproved = useMemo(
        () =>
            extracted.images.reduce(
                (sum, img) => sum + img.items.filter((i) => i.approved && i.title?.trim()).length,
                0,
            ),
        [extracted.images],
    );

    const totalItems = useMemo(
        () => extracted.images.reduce((sum, img) => sum + img.items.length, 0),
        [extracted.images],
    );

    return (
        <>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                    flexWrap: "wrap",
                    gap: 10,
                }}
            >
                <div style={{ color: "#555" }}>
                    تم استخراج <strong>{totalItems}</strong> عنصر من{" "}
                    <strong>{extracted.images.length}</strong> صورة • معتمد للحفظ:{" "}
                    <strong>{totalApproved}</strong>
                </div>
                <button
                    type="button"
                    className="button"
                    style={{ background: "#888" }}
                    onClick={onStartOver}
                    disabled={committing}
                >
                    البدء من جديد
                </button>
            </div>

            {extracted.images.map((img, imgIdx) => (
                <div className="review-image" key={`${img.filename}-${imgIdx}`}>
                    <div className="review-image-header">
                        <div className="filename">{img.filename}</div>
                        {img.error ? (
                            <div className="image-error">⚠ فشل استخراج هذه الصورة: {img.error}</div>
                        ) : null}
                        {img.notes ? (
                            <div className="image-error" style={{ color: "#7a5d00" }}>
                                ملاحظة: {img.notes}
                            </div>
                        ) : null}
                    </div>

                    <div className="review-image-body">
                        {img.previewUrl ? (
                            <div className="preview">
                                <img src={img.previewUrl} alt={img.filename} />
                            </div>
                        ) : (
                            <div className="preview" />
                        )}

                        <div>
                            {img.items.length === 0 ? (
                                <p style={{ color: "#888" }}>
                                    لم يتم استخراج أي عناصر من هذه الصورة.
                                </p>
                            ) : (
                                <>
                                    <table className="items-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 60 }}>اعتمد</th>
                                                <th>الاسم</th>
                                                <th>الوصف</th>
                                                <th style={{ width: 100 }}>السعر (₪)</th>
                                                <th style={{ minWidth: 200 }}>القسم</th>
                                                <th style={{ width: 70 }}>الثقة</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {img.items.map((it, itemIdx) => (
                                                <tr key={itemIdx}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!it.approved}
                                                            onChange={(e) =>
                                                                updateItem(imgIdx, itemIdx, {
                                                                    approved: e.target.checked,
                                                                })
                                                            }
                                                            disabled={committing}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={it.title ?? ""}
                                                            onChange={(e) =>
                                                                updateItem(imgIdx, itemIdx, {
                                                                    title: e.target.value,
                                                                })
                                                            }
                                                            disabled={committing}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={it.description ?? ""}
                                                            onChange={(e) =>
                                                                updateItem(imgIdx, itemIdx, {
                                                                    description: e.target.value,
                                                                })
                                                            }
                                                            disabled={committing}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            value={
                                                                typeof it.price === "number"
                                                                    ? it.price
                                                                    : ""
                                                            }
                                                            onChange={(e) =>
                                                                updateItem(imgIdx, itemIdx, {
                                                                    price:
                                                                        e.target.value === ""
                                                                            ? null
                                                                            : Number(
                                                                                  e.target.value,
                                                                              ),
                                                                })
                                                            }
                                                            disabled={committing}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            options={categoryOptions}
                                                            placeholder={
                                                                it.suggested_category_name
                                                                    ? `← اقتراح: ${it.suggested_category_name}`
                                                                    : "اختر القسم"
                                                            }
                                                            value={
                                                                categoryOptions.find(
                                                                    (o) =>
                                                                        o.value ===
                                                                        it.mappedCategoryId,
                                                                ) ?? null
                                                            }
                                                            onChange={(s) =>
                                                                updateItem(imgIdx, itemIdx, {
                                                                    mappedCategoryId:
                                                                        s?.value ?? null,
                                                                })
                                                            }
                                                            isClearable
                                                            isSearchable
                                                            isDisabled={committing}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                menuPortal: (base) => ({
                                                                    ...base,
                                                                    zIndex: 9999,
                                                                }),
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`confidence ${confidenceClass(it.confidence)}`}
                                                        >
                                                            {confidenceLabel(it.confidence)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="bulk-actions">
                                        <button
                                            type="button"
                                            className="button"
                                            onClick={() =>
                                                setBulkApprovedForImage(imgIdx, () => true)
                                            }
                                            disabled={committing}
                                        >
                                            اعتمد الكل
                                        </button>
                                        <button
                                            type="button"
                                            className="button"
                                            style={{ background: "#888" }}
                                            onClick={() =>
                                                setBulkApprovedForImage(imgIdx, () => false)
                                            }
                                            disabled={committing}
                                        >
                                            ارفض الكل
                                        </button>
                                        <button
                                            type="button"
                                            className="button"
                                            onClick={() =>
                                                setBulkApprovedForImage(
                                                    imgIdx,
                                                    (it) =>
                                                        typeof it.confidence === "number" &&
                                                        it.confidence >= 0.85,
                                                )
                                            }
                                            disabled={committing}
                                        >
                                            اعتمد عالي الثقة فقط
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ))}

            <div className="actions">
                <button
                    type="button"
                    className="button"
                    onClick={onCommit}
                    disabled={committing || totalApproved === 0}
                    style={{
                        minWidth: 260,
                        opacity: totalApproved === 0 ? 0.6 : 1,
                        cursor: totalApproved === 0 ? "not-allowed" : "pointer",
                    }}
                >
                    {committing ? "جارٍ الحفظ..." : `حفظ ${totalApproved} منتج`}
                </button>
            </div>
        </>
    );
};

export default ReviewView;
